<template>
  <div class="custom-container">
    <div class="ms-cell-editor combobox" :class="{'cell-error': errorMessage}">
      <div
        v-show="!isSelected || readOnly"
        :class="{
          'edit-disabled': readOnly,
          'ms-editor-text': editable,
          hasError: errorProvider.isValid,
        }"
      >
        <a v-show="!isEditor" @click="$emit('editorClick', $event, col, colIndex)" class="editor-click">
          <span
            class="editor-display"
            :title="[
              $ms.format.formatData(dataRow[col.dataField], {
                formatType: col.formatType,
                enumName: col.enum,
              }),
            ]"
            >{{
              $ms.format.formatData(dataRow[col.dataField], {
                formatType: col.formatType,
                enumName: col.enum,
              })
            }}</span
          >
        </a>

        <ms-combobox-enum
          v-model:display="dataRow[col.dataField]"
          v-model="dataRow[modelValueField]"
          v-if="isEditor"
          :class="['input-editor']"
          :column="propsData.columns"
          v-on="listeners"
          :initText="initText"
          :enum="propsData.enum"
          :isGridEditor="true"
          :customData="propsData.customData"
          :values="propsData.values"
          ref="msCombobox"
          :dropdownWidth="propsData.dropdownWidth"
          :preventPaste="allowCopyFromExcel"
        ></ms-combobox-enum>

        <div class="icon24 error mr-4px" v-show="errorMessage && !isEditor" v-tooltip="errorMessage"></div>
      </div>
      <div class="ms-editor control-cell" ref="editor" v-show="isSelected && !readOnly"></div>
    </div>

    <v-menu
      ref="optionMenu"
      class="option-wrapper"
      v-if="isEditor && isCopyDataColumn"
      popperClass="option-menu"
      :placement="isAtLastColumn ? 'bottom-start' : 'bottom-end'"
      :skidding="marginSetting"
      :width="menuContentWidth"
      :style="[{right: isAtLastColumn ? 'auto' : optionMenuRight}, {left: isAtLastColumn ? optionMenuLeft : 'auto'}]"
      @show="handleMenuShow"
      :class="dbOptionHeight"
    >
      <div :class="['icon24', 'option-tab-vertical', 'option-custom']"></div>
      <template #popper>
        <menu-item
          v-for="(item, i) in editorActions"
          :key="i"
          @menu-item-click="clickMenuItem(item.id, rowIndex, colIndex)"
          >{{ item.text }}</menu-item
        >
      </template>
    </v-menu>
  </div>
</template>
<script>
import {watch, nextTick, defineExpose, onMounted, getCurrentInstance, ref, computed, inject} from 'vue';
import {useCellEditor} from './CellEditorSetup.js';
import MenuItem from '@/components/dropdown/MenuItem.vue';

export default {
  name: 'MsCellCombobox',
  components: {
    MenuItem,
  },
  props: {
    col: {},
    dataRow: {},
    rowIndex: {
      default: 0,
      type: Number,
    },
    colIndex: {
      default: 0,
      type: Number,
    },
    //Thuộc tính để cấu hình cho phép sửa trên grid hay không
    editable: {
      default: true,
      type: Boolean,
    },
    rowSelected: {
      default: null,
    },
    readOnly: {
      default: false,
      type: Boolean,
    },
    //có cho phép show text validate bên dưới cell k
    isShowText: {
      type: Boolean,
      default: false,
    },
    propsData: {
      type: Object,
      default: () => {},
    },
    rules: {
      type: Array,
      default: () => [],
    },
    // Hiện thị số dòng cho phép.
    lineBreak: {
      default: 1,
      type: Number,
    },
    /**
     * Cấu hình sao chép dữ liệu 1 cột xuống các cột khác (mục đích để dev phải check lại các màn hình đã làm để tránh lỗi dữ liệu)
     * dtnga3 (26.05.2023)
     */
    isCopyDataColumn: {
      default: false,
      type: Boolean,
    },
    optionMenuRight: {
      default: null,
      type: String,
    },
    optionMenuLeft: {
      default: null,
      type: String,
    },
    // Đánh dấu control nằm ở cột cuối hay không
    // dtnga3
    isAtLastColumn: {
      default: false,
      type: Boolean,
    },
    // Độ rộng menu content (cần set để tính toán vị trí của nó)
    // dtnga3
    menuContentWidth: {
      default: 194,
      type: Number,
    },
  },
  emits: ['copyActionClick', 'editorClick', 'pasteInput', 'update:modelValue'],
  setup(props, {emit}) {
    const {proxy} = getCurrentInstance();
    const {
      isEditor,
      errorProvider,
      isSelected,
      listeners,
      activeCell,
      inactiveCell,
      findEditor,
      validate,
      clearValidate,
      errorMessage,
      editorActions,
      clickMenuItem,
      marginSetting,
      handleMenuShow,
    } = useCellEditor({
      props,
      emit,
    });

    const getValue = () => {
      return props.dataRow[props.col.dataField];
    };
    const initText = ref('');

    watch(isEditor, newVal => {
      if (newVal) {
        initText.value = proxy.$ms.format.formatData(proxy.dataRow[proxy.col.dataField], {
          formatType: proxy.col.formatType,
          enumName: proxy.col.enum,
        });
        nextTick(() => {
          findEditor();
        });
      }
    });

    const allowCopyFromExcel = inject('allowCopyFromExcel');

    const modelValueField = props.propsData.modelValueField || props.propsData.valueField;
    const msCombobox = ref({});
    const getMetaData = () => {
      return msCombobox.value?.internalSelectedItem ? {...msCombobox.value.internalSelectedItem} : {};
    };
    const dbOptionHeight = computed(() => {
      return (
        'heightoptionvalue' +
        parseInt(
          proxy.$store?.state?.dbOption?.dbOptions.HeightOption == 1
            ? proxy.$store?.state?.dbOption?.dbOptions.HeightOption
            : 0,
        )
      );
    });

    /**
     * Gán giá trị cho cell
     */
    const setValue = value => {
      let me = proxy;

      if (me.readOnly) {
        return;
      }

      if (isEditor.value && typeof me.$refs.msCombobox?.setInternalText == 'function') {
        me.$refs.msCombobox.setInternalText(value);
      }

      let enumData = proxy.$ms.commonFn.getEnumSource(proxy.propsData.enum, proxy.propsData.values);

      let findEnum = enumData?.find(x => x.enumText?.trim() == value?.trim());

      if (findEnum) {
        me.dataRow[me.col.dataField] = value;
        me.dataRow[modelValueField] = findEnum.enumValue;
      } else {
        me.dataRow[me.col.dataField] = null;
        me.dataRow[modelValueField] = null;
      }

      me.$emit('update:modelValue', me.dataRow[me.col.dataField]);
    };

    return {
      isEditor,
      errorProvider,
      isSelected,
      listeners,
      activeCell,
      inactiveCell,
      findEditor,
      getValue,
      validate,
      clearValidate,
      errorMessage,
      initText,
      modelValueField,
      getMetaData,
      msCombobox,
      editorActions,
      clickMenuItem,
      marginSetting,
      handleMenuShow,
      dbOptionHeight,
      setValue,
      allowCopyFromExcel,
    };
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/components/CellAction.scss';
</style>
